import { useState, useEffect, useCallback } from 'react';
import { cva } from 'class-variance-authority';
import { commands } from '@/lib/platform/generated/bindings.gen';
import { clsx } from 'clsx';
import { Flex, Text, Button } from '@nula/core-ui';
import styles from './shortcut-capture.module.css';

export type ShortcutCaptureProps = {
  current: string;
  onChange: (shortcut: string) => void;
  className?: string;
};

const root = cva(styles.root);
const captureButton = cva(styles.captureButton);

/** Build a human-readable shortcut string from a KeyboardEvent. */
function buildShortcut(e: KeyboardEvent): string | null {
  // Ignore bare modifier keypresses
  if (['Meta', 'Control', 'Alt', 'Shift'].includes(e.key)) return null;

  const parts: string[] = [];
  if (e.metaKey) parts.push('Cmd');
  if (e.ctrlKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');

  const key = e.key === ' ' ? 'Space' : e.key;
  parts.push(key.length === 1 ? key.toUpperCase() : key);

  return parts.join('+');
}

export function ShortcutCapture({ current, onChange, className }: ShortcutCaptureProps) {
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.key === 'Escape') {
        setCapturing(false);
        setError(null);
        return;
      }

      const shortcut = buildShortcut(e);
      if (!shortcut) return;

      commands.validateShortcut(shortcut)
        .then((res) => {
          if (res.status === 'ok') {
            onChange(shortcut);
            setCapturing(false);
            setError(null);
          } else {
            // status === 'error': shortcut was rejected by the Rust validator
            setError(String(res.error));
          }
        })
        .catch((err: unknown) => {
          // Unexpected throw (e.g. IPC failure)
          setError(err instanceof Error ? err.message : String(err));
        });
    },
    [onChange],
  );

  useEffect(() => {
    if (!capturing) return;
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [capturing, handleKeyDown]);

  return (
    <Flex vertical className={clsx(root(), className)}>
      <Text size="sm" className={styles.label}>Recording shortcut</Text>
      <Text type="secondary" size="xs">
        Global keyboard shortcut to start or stop recording.
      </Text>
      <Flex className={captureButton()}>
        <Button
          size="sm"
          onClick={() => {
            setCapturing(true);
            setError(null);
          }}
        >
          {capturing ? 'Press a key combo…' : current}
        </Button>
      </Flex>
      {error && (
        <Text type="danger" size="sm" role="alert">
          {error}
        </Text>
      )}
    </Flex>
  );
}
